import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  CircularProgress,
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import PropTypes from "prop-types";
import Iconify from "src/components/iconify";
import EvaPlusFill from "@iconify/icons-eva/plus-fill";
import EvaAlertTriangleFill from "@iconify/icons-eva/alert-triangle-fill";
import EvaArrowIosUpwardFill from "@iconify/icons-eva/arrow-ios-upward-fill";
import EvaArrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import EvaEdit2Fill from "@iconify/icons-eva/edit-2-fill";
import EvaTrash2Fill from "@iconify/icons-eva/trash-2-fill";
import EvaCopyFill from "@iconify/icons-eva/copy-fill";
import { ITEM_INITIAL_VALUE } from "../index";
import { empleadosApi } from "src/services/empleadosApi";
import { tiposApi } from "src/services/tiposApi";
import { generarUuid } from "../../EditarPropuestaV2/utils/generarUuid";
import FaseEditDialog from "./FaseEditDialog";
import { BRAND } from "src/theme/brandTokens";

/**
 * Paso 5 — Fases y Servicios
 *
 * Lista compacta de fases con edición en Dialog (dos tabs: PDF / Operacional).
 * Sin datos volcados en pantalla — todo queda limpio y escaneable.
 */
const StepFasesServicios = ({
  formik,
  razonesSeleccionIA,
  limpiarRazonSeleccionIA,
  resetRazonesSeleccionIA,
  swapRazonesSeleccionIA,
}) => {
  const sucursal = formik.values.sucursal;
  const items = formik.values.items;

  const [triggerEmpleados, { data: empleados = [] }] =
    empleadosApi.useLazyGetEmpleadosActivosQuery();

  useEffect(() => {
    if (sucursal) triggerEmpleados({ id: sucursal, estado: "ALL" });
  }, [sucursal, triggerEmpleados]);

  // Catalogos de servicios (cacheados por RTK). Incluyen la config de
  // encargado por defecto y bloqueo de asignacion por servicio.
  const { data: catalogoPlanes = [], isLoading: cargandoPlanes } =
    tiposApi.useGetTipoPlanesQuery();
  const { data: catalogoTareas = [], isLoading: cargandoTareas } =
    tiposApi.useGetTipoTareasQuery();
  const cargandoCatalogos = cargandoPlanes || cargandoTareas;

  const configPlanesPorNombre = useMemo(() => {
    const m = new Map();
    catalogoPlanes.forEach((p) => m.set(p.nombre, p));
    return m;
  }, [catalogoPlanes]);
  const configTareasPorNombre = useMemo(() => {
    const m = new Map();
    catalogoTareas.forEach((t) => m.set(t.nombre, t));
    return m;
  }, [catalogoTareas]);

  // Asegura el encargado por defecto del servicio en items que no pasaron por la
  // seleccion manual (IA, edicion, duplicado). Si el servicio bloquea la
  // asignacion se fuerza el default; si solo sugiere, se rellena cuando esta vacio.
  useEffect(() => {
    if (!items.length) return;
    let cambio = false;
    const normalizados = items.map((it) => {
      const cfg =
        it.tipoItem === "PLAN"
          ? configPlanesPorNombre.get(it.refTipoPlan)
          : configTareasPorNombre.get(it.refTipoTarea);
      const def = cfg?.empleado_encargado_default;
      if (!def) return it;
      const nuevo = cfg.bloquear_asignacion ? def : it.refEmpleado || def;
      if (nuevo !== it.refEmpleado) {
        cambio = true;
        return { ...it, refEmpleado: nuevo };
      }
      return it;
    });
    if (cambio) formik.setFieldValue("items", normalizados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, configPlanesPorNombre, configTareasPorNombre]);

  // Dialog de edición
  const [dialogIndex, setDialogIndex] = useState(null); // índice de la fase en edición
  const [isNew, setIsNew] = useState(false); // true cuando se está creando una nueva

  const abrirNueva = () => {
    formik.setFieldValue("items", [
      ...items,
      { ...ITEM_INITIAL_VALUE, __key: generarUuid() },
    ]);
    setDialogIndex(items.length);
    setIsNew(true);
  };

  const handleDuplicar = (index) => {
    const original = items[index];
    const copia = { ...original, __key: generarUuid() };
    formik.setFieldValue("items", [
      ...items.slice(0, index + 1),
      copia,
      ...items.slice(index + 1),
    ]);
    // Insertar en index+1 desplaza todos los índices siguientes — el mapa
    // razonesSeleccionIA queda inconsistente. Se descarta completo.
    resetRazonesSeleccionIA();
  };

  const abrirEditar = (index) => {
    setDialogIndex(index);
    setIsNew(false);
  };

  const handleCerrar = () => {
    // Si se canceló una fase nueva (sin guardar), la removemos
    if (isNew && dialogIndex === items.length - 1) {
      formik.setFieldValue("items", items.slice(0, -1));
    }
    setDialogIndex(null);
    setIsNew(false);
  };

  const handleGuardar = (itemEditado) => {
    const nuevosItems = items.map((it, i) =>
      i === dialogIndex ? itemEditado : it,
    );
    formik.setFieldValue("items", nuevosItems);
    // El vendor modificó el item manualmente — limpiar la razon sugerida por IA.
    limpiarRazonSeleccionIA(dialogIndex);
    setDialogIndex(null);
    setIsNew(false);
  };

  const handleEliminar = (index) => {
    formik.setFieldValue(
      "items",
      items.filter((_, i) => i !== index),
    );
    // Al eliminar un item, filter desplaza todos los índices siguientes — el
    // mapa razonesSeleccionIA queda inconsistente. Se descarta completo.
    resetRazonesSeleccionIA();
  };

  const handleMoverArriba = (index) => {
    if (index === 0) return;
    const arr = [...items];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    formik.setFieldValue("items", arr);
    // Los items intercambiaron posición — mover sus razones con ellos.
    swapRazonesSeleccionIA(index - 1, index);
  };

  const handleMoverAbajo = (index) => {
    if (index === items.length - 1) return;
    const arr = [...items];
    [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
    formik.setFieldValue("items", arr);
    // Los items intercambiaron posición — mover sus razones con ellos.
    swapRazonesSeleccionIA(index, index + 1);
  };

  const formatValor = (item) => {
    const v = Number(item.valorConDescuento || item.valorOriginal);
    if (!v) return null;
    return `${item.tipoMoneda || ""} ${v.toLocaleString("es-CL")}`.trim();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Fases y Servicios
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Agrega cada servicio o fase de la propuesta. Haz clic en una fase para
        editarla.
      </Typography>

      {/* Indicador no bloqueante mientras carga la config de servicios */}
      {cargandoCatalogos && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="textSecondary">
            Cargando configuración de servicios...
          </Typography>
        </Box>
      )}

      {/* ── Lista de fases ────────────────────────────────────────────── */}
      {items.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: "center",
            borderStyle: "dashed",
            borderColor: "grey.300",
            mb: 2,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Aún no hay fases. Haz clic en "Agregar fase" para comenzar.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mb: 2 }}>
          {items.map((item, index) => {
            const titulo =
              item.faseTitulo || item.nombreServicio || `Fase ${index + 1}`;
            const clienteNombre = item.refCliente || null;
            const valor = formatValor(item);
            const completa =
              item.refCliente &&
              item.refEmpleado &&
              (item.tipoItem !== "PLAN" || item.refTipoPlan);

            return (
              <Paper
                key={item.__key ?? index}
                id={`item-${index}`}
                variant="outlined"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  mb: 1,
                  cursor: "pointer",
                  transition: "box-shadow 0.15s",
                  "&:hover": { boxShadow: 2 },
                  borderColor: !completa ? "warning.light" : "divider",
                }}
                onClick={() => abrirEditar(index)}
              >
                {/* Número */}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: BRAND.color.primary,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>

                {/* Tipo */}
                <Chip
                  label={item.tipoItem}
                  size="small"
                  sx={{
                    bgcolor:
                      item.tipoItem === "PLAN"
                        ? "#E3F2FD"
                        : BRAND.color.primaryBg,
                    color:
                      item.tipoItem === "PLAN"
                        ? "#1565C0"
                        : BRAND.color.primary,
                    fontWeight: 600,
                    flexShrink: 0,
                    fontSize: 11,
                  }}
                />

                {/* Título */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {titulo}
                    </Typography>
                    {razonesSeleccionIA[index] && (
                      <Tooltip
                        title={razonesSeleccionIA[index]}
                        arrow
                        placement="top"
                      >
                        <InfoOutlined
                          fontSize="small"
                          sx={{ color: "primary.main", flexShrink: 0 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  {clienteNombre && (
                    <Typography variant="caption" color="textSecondary" noWrap>
                      {clienteNombre}
                    </Typography>
                  )}
                </Box>

                {/* Valor */}
                {valor && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: BRAND.color.primary,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {valor}
                  </Typography>
                )}

                {/* Indicador de completitud */}
                {!completa && (
                  <Tooltip title="Faltan datos requeridos — haz clic para completar">
                    <Iconify
                      icon={EvaAlertTriangleFill}
                      width={18}
                      height={18}
                      style={{ color: "#FF9800", flexShrink: 0 }}
                    />
                  </Tooltip>
                )}

                {/* Reordenar */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexShrink: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleMoverArriba(index)}
                    disabled={index === 0}
                    sx={{ p: 0.25 }}
                  >
                    <Iconify
                      icon={EvaArrowIosUpwardFill}
                      width={16}
                      height={16}
                    />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleMoverAbajo(index)}
                    disabled={index === items.length - 1}
                    sx={{ p: 0.25 }}
                  >
                    <Iconify
                      icon={EvaArrowIosDownwardFill}
                      width={16}
                      height={16}
                    />
                  </IconButton>
                </Box>

                {/* Editar */}
                <Tooltip title="Editar fase">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirEditar(index);
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    <Iconify icon={EvaEdit2Fill} width={18} height={18} />
                  </IconButton>
                </Tooltip>

                {/* Duplicar */}
                <Tooltip title="Duplicar fase">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicar(index);
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    <Iconify icon={EvaCopyFill} width={18} height={18} />
                  </IconButton>
                </Tooltip>

                {/* Eliminar */}
                <Tooltip title="Eliminar fase">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEliminar(index);
                    }}
                    sx={{ flexShrink: 0, color: "error.main" }}
                  >
                    <Iconify icon={EvaTrash2Fill} width={18} height={18} />
                  </IconButton>
                </Tooltip>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Botón agregar */}
      <Button
        variant="outlined"
        startIcon={<Iconify icon={EvaPlusFill} width={18} height={18} />}
        onClick={abrirNueva}
        sx={{
          borderColor: BRAND.color.primary,
          color: BRAND.color.primary,
          "&:hover": {
            borderColor: BRAND.color.primaryDark,
            color: BRAND.color.primaryDark,
          },
        }}
      >
        Agregar fase / servicio
      </Button>

      {/* Dialog de edición */}
      {dialogIndex !== null && dialogIndex < items.length && (
        <FaseEditDialog
          open
          onClose={handleCerrar}
          item={items[dialogIndex]}
          itemIndex={dialogIndex}
          onSave={handleGuardar}
          onDelete={() => {
            handleEliminar(dialogIndex);
            setDialogIndex(null);
            setIsNew(false);
          }}
          empleados={empleados}
          sucursal={sucursal}
          isNew={isNew}
        />
      )}
    </Box>
  );
};

StepFasesServicios.propTypes = {
  formik: PropTypes.object.isRequired,
  razonesSeleccionIA: PropTypes.object.isRequired,
  limpiarRazonSeleccionIA: PropTypes.func.isRequired,
  resetRazonesSeleccionIA: PropTypes.func.isRequired,
  swapRazonesSeleccionIA: PropTypes.func.isRequired,
};

export default StepFasesServicios;
